import { z } from 'zod';

/**
 * Tenant-scoped roles — only two valid values. See models/src/auth/types.ts.
 */
export const TenantRoleSchema = z.enum(['owner', 'admin']);

/**
 * Restaurant-scoped roles. `accountant` is a Books-only role added in
 * Phase 7 of the OrderUp Books rebuild — see
 * `packages/models/src/auth/types.ts`.
 */
export const LocationRoleSchema = z.enum(['manager', 'staff', 'accountant']);

/**
 * Any role value — union of tenant and restaurant roles.
 */
export const AnyRoleSchema = z.enum([
  'owner',
  'admin',
  'manager',
  'accountant',
  'staff',
]);

/**
 * Scope discriminator for an Assignment.
 */
export const AssignmentScopeSchema = z.enum(['tenant', 'restaurant']);

/**
 * Provenance of an assignment grant. Optional on the wire/doc — an absent
 * value means `'manual'` (operator-owned). Only `'job_title'` grants are
 * auto-maintained by the JobTitle-capability reconciler. Keep in lockstep with
 * `AssignmentSource` in `packages/models/src/auth/Assignment.ts`.
 */
export const AssignmentSourceSchema = z.enum(['manual', 'job_title', 'system']);

/**
 * Lifecycle status of an assignment.
 */
export const AssignmentStatusSchema = z.enum(['active', 'suspended', 'revoked']);

/**
 * The operator-owned grant a `job_title` assignment displaced. Mirrors
 * `SupersededGrant` in `packages/models/src/auth/Assignment.ts`.
 */
export const SupersededGrantSchema = z
  .object({
    role: AnyRoleSchema,
    grantSource: AssignmentSourceSchema,
    grantedByUid: z.string().min(1, { message: 'grantedByUid is required' }),
    grantedAt: z.date(),
  })
  .strict();

/**
 * A single Assignment on a User document. Current state only — lifecycle
 * history lives in the `assignment_history` subcollection.
 */
export const AssignmentSchema = z
  .object({
    scope: AssignmentScopeSchema,
    tenantDocId: z.string().min(1, { message: 'tenantDocId is required' }),
    restaurantDocId: z.string().min(1).optional(),
    role: AnyRoleSchema,
    status: AssignmentStatusSchema.default('active'),
    isActive: z.boolean(),
    grantedByUid: z.string().min(1, { message: 'grantedByUid is required' }),
    grantedAt: z.date(),
    updatedAt: z.date(),
    // Provenance of the grant. Optional for backward compatibility (absent ⇒
    // 'manual'). Only 'job_title' grants are auto-maintained by the
    // JobTitle-capability reconciler.
    grantSource: AssignmentSourceSchema.optional(),
    // The operator-owned grant this `job_title` assignment displaced, restored
    // verbatim when the capability is removed. Present ONLY on a superseding
    // `job_title` grant. Keep in lockstep with `SupersededGrant` in
    // `packages/models/src/auth/Assignment.ts` — this schema is `.strict()`, so
    // a field written without being declared here fails EVERY subsequent user
    // document write, not just this one.
    supersedes: SupersededGrantSchema.optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.scope === 'tenant') {
      if (val.role !== 'owner' && val.role !== 'admin') {
        ctx.addIssue({
          code: 'custom',
          message: `scope='tenant' requires role ∈ {owner, admin}, got '${val.role}'`,
          path: ['role'],
        });
      }
      if (val.restaurantDocId !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: `scope='tenant' must not set restaurantDocId`,
          path: ['restaurantDocId'],
        });
      }
    } else {
      if (
        val.role !== 'manager' &&
        val.role !== 'staff' &&
        val.role !== 'accountant'
      ) {
        ctx.addIssue({
          code: 'custom',
          message: `scope='restaurant' requires role ∈ {manager, accountant, staff}, got '${val.role}'`,
          path: ['role'],
        });
      }
      if (!val.restaurantDocId) {
        ctx.addIssue({
          code: 'custom',
          message: `scope='restaurant' requires restaurantDocId`,
          path: ['restaurantDocId'],
        });
      }
    }
    // status and isActive must be consistent
    const expectedActive = val.status === 'active';
    if (val.isActive !== expectedActive) {
      ctx.addIssue({
        code: 'custom',
        message: `isActive must be ${expectedActive} when status is '${val.status}'`,
        path: ['isActive'],
      });
    }
  });

/**
 * Request body schema for granting an assignment. The server fills in
 * grantedByUid / grantedAt / updatedAt / isActive from AuthContext and clock.
 */
export const GrantAssignmentRequestSchema = z.discriminatedUnion('scope', [
  z
    .object({
      scope: z.literal('tenant'),
      tenantDocId: z.string().min(1),
      role: TenantRoleSchema,
      /** Either target an existing user by uid or by email (server resolves). */
      targetUid: z.string().min(1).optional(),
      targetEmail: z.string().email().optional(),
    })
    .strict()
    .refine((v) => !!v.targetUid || !!v.targetEmail, {
      message: 'Either targetUid or targetEmail is required',
      path: ['targetUid'],
    }),
  z
    .object({
      scope: z.literal('restaurant'),
      tenantDocId: z.string().min(1),
      restaurantDocId: z.string().min(1),
      role: LocationRoleSchema,
      targetUid: z.string().min(1).optional(),
      targetEmail: z.string().email().optional(),
    })
    .strict()
    .refine((v) => !!v.targetUid || !!v.targetEmail, {
      message: 'Either targetUid or targetEmail is required',
      path: ['targetUid'],
    }),
]);

/**
 * Request body schema for revoking an assignment.
 */
export const RevokeAssignmentRequestSchema = z.discriminatedUnion('scope', [
  z
    .object({
      scope: z.literal('tenant'),
      targetUid: z.string().min(1),
      tenantDocId: z.string().min(1),
      reason: z.string().min(1).max(500).optional(),
    })
    .strict(),
  z
    .object({
      scope: z.literal('restaurant'),
      targetUid: z.string().min(1),
      tenantDocId: z.string().min(1),
      restaurantDocId: z.string().min(1),
      reason: z.string().min(1).max(500).optional(),
    })
    .strict(),
]);

/**
 * Request body schema for suspending an assignment.
 */
export const SuspendAssignmentRequestSchema = z.discriminatedUnion('scope', [
  z
    .object({
      scope: z.literal('tenant'),
      targetUid: z.string().min(1),
      tenantDocId: z.string().min(1),
      reason: z.string().min(1).max(500).optional(),
    })
    .strict(),
  z
    .object({
      scope: z.literal('restaurant'),
      targetUid: z.string().min(1),
      tenantDocId: z.string().min(1),
      restaurantDocId: z.string().min(1),
      reason: z.string().min(1).max(500).optional(),
    })
    .strict(),
]);

export type AssignmentType = z.infer<typeof AssignmentSchema>;
export type GrantAssignmentRequestType = z.infer<typeof GrantAssignmentRequestSchema>;
export type RevokeAssignmentRequestType = z.infer<typeof RevokeAssignmentRequestSchema>;
export type SuspendAssignmentRequestType = z.infer<typeof SuspendAssignmentRequestSchema>;
